import React from 'react';

const WIDTH = 640;
const HEIGHT = 360;
const LEFT = 48;
const TOP = 32;
const RIGHT = 16;
const BOTTOM = 44;
const MAX_POINTS_PER_TRACE = 512;
const COLORS = ['#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed', '#0891b2'];

const finiteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const downsample = (points) => {
    if (points.length <= MAX_POINTS_PER_TRACE) {
        return points;
    }
    const stride = Math.ceil(points.length / MAX_POINTS_PER_TRACE);
    const sampled = points.filter((point, index) => index % stride === 0).slice(0, MAX_POINTS_PER_TRACE);
    const last = points[points.length - 1];
    if (sampled[sampled.length - 1] !== last && sampled.length < MAX_POINTS_PER_TRACE) {
        sampled.push(last);
    }
    return sampled;
};

const tracePoints = (trace) => {
    if (!trace || typeof trace !== 'object' || !Array.isArray(trace.x)) {
        return [];
    }
    if (trace.type === 'histogram') {
        return downsample(
            trace.x.filter(finiteNumber).map((value, index) => [index, value]),
        );
    }
    if (!Array.isArray(trace.y) || trace.x.length !== trace.y.length) {
        return [];
    }
    return downsample(
        trace.x.reduce((points, xValue, index) => {
            const yValue = trace.y[index];
            if (finiteNumber(xValue) && finiteNumber(yValue)) {
                points.push([xValue, yValue]);
            }
            return points;
        }, []),
    );
};

export const staticGraphGeometry = (spec) => {
    if (!spec || !Array.isArray(spec.data)) {
        return null;
    }
    const traces = spec.data.map(tracePoints).filter((points) => points.length > 0);
    if (traces.length === 0) {
        return null;
    }
    const allPoints = traces.reduce((points, trace) => points.concat(trace), []);
    const xValues = allPoints.map(([x]) => x);
    const yValues = allPoints.map(([, y]) => y);
    let xMin = Math.min(...xValues);
    let xMax = Math.max(...xValues);
    let yMin = Math.min(...yValues);
    let yMax = Math.max(...yValues);
    if (xMin === xMax) {
        xMin -= 1;
        xMax += 1;
    }
    if (yMin === yMax) {
        yMin -= 1;
        yMax += 1;
    }
    const plotWidth = WIDTH - LEFT - RIGHT;
    const plotHeight = HEIGHT - TOP - BOTTOM;
    const project = ([xValue, yValue]) => [
        Number((LEFT + ((xValue - xMin) / (xMax - xMin)) * plotWidth).toFixed(2)),
        Number((HEIGHT - BOTTOM - ((yValue - yMin) / (yMax - yMin)) * plotHeight).toFixed(2)),
    ];
    return {
        traces: traces.map((points) => points.map(project)),
        title: typeof spec.layout?.title?.text === 'string' ? spec.layout.title.text.slice(0, 160) : 'Metric graph',
    };
};

const StaticMetricGraph = ({ spec, label }) => {
    const geometry = staticGraphGeometry(spec);
    if (!geometry) {
        return <span role="status">Metric graph is unavailable.</span>;
    }
    const accessibleLabel = typeof label === 'string' && label.trim() ? label.trim().slice(0, 160) : geometry.title;
    return (
        <svg
            data-testid="static-metric-graph"
            width="640"
            height="360"
            viewBox="0 0 640 360"
            role="img"
            aria-label={accessibleLabel}
            style={{ width: '100%', height: '320px' }}
        >
            <title>{geometry.title}</title>
            <rect width={WIDTH} height={HEIGHT} fill="#ffffff" />
            <line x1={LEFT} y1={HEIGHT - BOTTOM} x2={WIDTH - RIGHT} y2={HEIGHT - BOTTOM} stroke="#64748b" />
            <line x1={LEFT} y1={TOP} x2={LEFT} y2={HEIGHT - BOTTOM} stroke="#64748b" />
            {geometry.traces.map((points, index) => (
                <polyline
                    key={`static-trace-${index}`}
                    points={points.map(([x, y]) => `${x},${y}`).join(' ')}
                    fill="none"
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth="2"
                />
            ))}
        </svg>
    );
};

export { StaticMetricGraph };
